import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { ShoppingCart, ArrowLeft, CheckCircle, Tag, Truck, Shield, Package, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { useCart } from '../context/CartContext';
import Header from '../components/Header';
import Footer from '../components/Footer';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const SparePartDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { addToCart } = useCart();
  const [part, setPart] = useState(null);
  const [loading, setLoading] = useState(true);
  const [relatedParts, setRelatedParts] = useState([]);
  const [qty, setQty] = useState(1);

  useEffect(() => {
    const fetchPart = async () => {
      try {
        const res = await fetch(`${BACKEND_URL}/api/spare-parts/${id}`);
        if (res.ok) {
          const data = await res.json();
          setPart(data);
          // Fetch related parts from same category
          const relRes = await fetch(`${BACKEND_URL}/api/spare-parts?category=${encodeURIComponent(data.category)}`);
          if (relRes.ok) {
            const relData = await relRes.json();
            setRelatedParts(relData.filter(p => p.id !== id).slice(0, 4));
          }
        } else {
          navigate('/spare-parts');
        }
      } catch { navigate('/spare-parts'); }
      finally { setLoading(false); }
    };
    fetchPart();
    window.scrollTo(0, 0);
  }, [id, navigate]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="flex items-center justify-center py-32">
          <Loader2 className="w-10 h-10 text-lime-600 animate-spin" />
        </div>
        <Footer />
      </div>
    );
  }

  if (!part) return null;

  const discount = part.originalPrice > part.price
    ? Math.round(((part.originalPrice - part.price) / part.originalPrice) * 100)
    : 0;

  return (
    <div className="min-h-screen bg-gray-50" data-testid="spare-part-detail-page">
      <Header />

      {/* Breadcrumb */}
      <div className="bg-white border-b">
        <div className="container mx-auto px-4 py-3">
          <div className="flex items-center gap-2 text-sm text-gray-500">
            <Link to="/" className="hover:text-gray-900">Home</Link>
            <span>/</span>
            <Link to="/spare-parts" className="hover:text-gray-900">Spare Parts</Link>
            <span>/</span>
            <span className="text-gray-900 font-medium">{part.name}</span>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 py-8">
        <div className="grid lg:grid-cols-2 gap-8 max-w-6xl mx-auto">
          {/* Image */}
          <div className="bg-white rounded-2xl border p-6 flex items-center justify-center">
            {part.image ? (
              <img src={part.image} alt={part.name}
                className="max-h-[400px] w-full object-contain rounded-xl"
                data-testid="spare-part-image" />
            ) : (
              <div className="w-full h-[400px] bg-gray-100 rounded-xl flex items-center justify-center">
                <Package className="w-20 h-20 text-gray-300" />
              </div>
            )}
          </div>

          {/* Details */}
          <div className="space-y-5">
            <div>
              <span className="inline-block px-3 py-1 bg-lime-100 text-lime-700 text-xs font-semibold rounded-full uppercase mb-2"
                data-testid="spare-part-category">
                {part.category}
              </span>
              <h1 className="text-2xl md:text-3xl font-bold text-gray-900 mb-2" data-testid="spare-part-name">
                {part.name}
              </h1>
            </div>

            {/* Price */}
            <div className="flex items-end gap-3" data-testid="spare-part-price">
              <span className="text-3xl font-bold text-gray-900">{'\u20B9'}{part.price}</span>
              {part.originalPrice > part.price && (
                <>
                  <span className="text-xl text-gray-400 line-through">{'\u20B9'}{part.originalPrice}</span>
                  <span className="px-2 py-0.5 bg-red-100 text-red-700 text-sm font-bold rounded">{discount}% OFF</span>
                </>
              )}
            </div>

            {/* Stock Status */}
            <div className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-medium ${
              part.inStock ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'
            }`} data-testid="spare-part-stock">
              <span className={`w-2 h-2 rounded-full ${part.inStock ? 'bg-green-500' : 'bg-red-500'}`} />
              {part.inStock ? 'In Stock' : 'Out of Stock'}
            </div>

            {/* Description */}
            {part.description && (
              <div data-testid="spare-part-description">
                <h3 className="text-sm font-semibold text-gray-900 mb-2">Description</h3>
                <p className="text-gray-600 text-sm leading-relaxed">{part.description}</p>
              </div>
            )}

            {/* Specifications */}
            <div className="bg-gray-50 rounded-xl p-4 space-y-3" data-testid="spare-part-specs">
              <h3 className="text-sm font-semibold text-gray-900">Specifications</h3>
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-500">Category</span>
                  <span className="font-medium text-gray-900">{part.category}</span>
                </div>
                {part.lifespan && (
                  <div className="flex justify-between">
                    <span className="text-gray-500">Lifespan</span>
                    <span className="font-medium text-gray-900">{part.lifespan}</span>
                  </div>
                )}
              </div>
            </div>

            {/* Compatible Brands */}
            {part.compatibility && part.compatibility.length > 0 && (
              <div data-testid="spare-part-compatibility">
                <h3 className="text-sm font-semibold text-gray-900 mb-2">Compatible Brands</h3>
                <div className="flex flex-wrap gap-2">
                  {part.compatibility.map(brand => (
                    <span key={brand} className="px-3 py-1.5 bg-white border border-gray-200 rounded-lg text-sm font-medium text-gray-700">
                      {brand}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Quantity & Add to Cart */}
            <div className="flex items-center gap-4 pt-2">
              <div className="flex items-center border rounded-lg overflow-hidden">
                <button onClick={() => setQty(q => Math.max(1, q - 1))}
                  className="px-3 py-2 bg-gray-50 hover:bg-gray-100 text-lg font-medium">-</button>
                <span className="px-4 py-2 text-sm font-medium" data-testid="spare-part-qty">{qty}</span>
                <button onClick={() => setQty(q => q + 1)}
                  className="px-3 py-2 bg-gray-50 hover:bg-gray-100 text-lg font-medium">+</button>
              </div>
              <Button onClick={() => {
                for (let i = 0; i < qty; i++) {
                  addToCart({ id: part.id, name: part.name, price: part.price, image: part.image, brand: part.category });
                }
              }}
                disabled={!part.inStock}
                className="flex-1 bg-lime-600 hover:bg-lime-700 text-white py-3 rounded-xl font-semibold text-base"
                data-testid="spare-part-add-to-cart">
                <ShoppingCart size={18} className="mr-2 inline" />
                {part.inStock ? 'Add to Cart' : 'Out of Stock'}
              </Button>
            </div>

            {/* Trust Badges */}
            <div className="grid grid-cols-3 gap-3 pt-3 border-t">
              <div className="text-center">
                <Truck className="w-5 h-5 mx-auto mb-1 text-blue-600" />
                <p className="text-[10px] sm:text-xs text-gray-600 font-medium">Free Delivery</p>
              </div>
              <div className="text-center">
                <Shield className="w-5 h-5 mx-auto mb-1 text-green-600" />
                <p className="text-[10px] sm:text-xs text-gray-600 font-medium">Genuine Parts</p>
              </div>
              <div className="text-center">
                <CheckCircle className="w-5 h-5 mx-auto mb-1 text-lime-600" />
                <p className="text-[10px] sm:text-xs text-gray-600 font-medium">Quality Assured</p>
              </div>
            </div>
          </div>
        </div>

        {/* Related Spare Parts */}
        {relatedParts.length > 0 && (
          <div className="max-w-6xl mx-auto mt-12">
            <h2 className="text-xl font-bold text-gray-900 mb-5" data-testid="related-spares-heading">Related Spare Parts</h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {relatedParts.map(rp => (
                <Link key={rp.id} to={`/spare-part/${rp.id}`}
                  className="bg-white rounded-xl border hover:shadow-lg transition-shadow overflow-hidden group"
                  data-testid={`related-spare-${rp.id}`}>
                  <div className="aspect-square bg-gray-50 overflow-hidden">
                    {rp.image ? (
                      <img src={rp.image} alt={rp.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-gray-300"><Tag size={32} /></div>
                    )}
                  </div>
                  <div className="p-3">
                    <p className="text-[10px] text-lime-600 font-semibold uppercase">{rp.category}</p>
                    <h3 className="text-xs sm:text-sm font-semibold text-gray-900 line-clamp-2 mt-0.5">{rp.name}</h3>
                    <div className="flex items-center gap-2 mt-1.5">
                      <span className="text-sm font-bold text-gray-900">{'\u20B9'}{rp.price}</span>
                      {rp.originalPrice > rp.price && (
                        <span className="text-xs text-gray-400 line-through">{'\u20B9'}{rp.originalPrice}</span>
                      )}
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        )}
      </div>

      <Footer />
    </div>
  );
};

export default SparePartDetailPage;
